import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { usePageNameContext } from "../../../contexts/PageTitleContext";
import useSettings from "../../../data/useSettings";
import useVatMarginScheme from "../../../data/useVatMarginScheme";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { ISetting, SettingInputType, SettingType } from "../../../models/Setting";
import { ButtonInput } from "../../../components/shared/Inputs/ButtonInput";
import { TextInput } from "../../../components/shared/Inputs/TextInput";
import LoadingWheel from "../../../components/shared/LoadingWheel";
import PriceFormat from "../../../utils/priceFormat";
import ConfirmModal from "../../../components/shared/Confirm/ConfirmModal";

const SCHEME_KEY = "Vat:GlobalMarginSchemeEnabled";
const RATE_KEY = "Vat:StandardRate";
const HIGH_VALUE_KEY = "Vat:MarginSchemeHighValueThreshold";
const HIGH_VALUE_AUTO_KEY = "Vat:MarginSchemeHighValueAutoStandard";
const OPENING_BALANCE_KEY = "Vat:MarginSchemeOpeningBalance";

type FormValues = {
  schemeEnabled: boolean;
  standardRate: string;
  highValueAutoStandard: boolean;
  highValueThreshold: string;
  openingBalance: string;
};

const toBool = (value?: string) => value?.toLowerCase() === "true";

export default function VatMarginSchemeSettings() {
  const { setInfo } = usePageNameContext();
  const { generalSettings, loading, update, create } = useSettings();
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const {
    ensureSettings,
    closeScheme,
    standardRate,
    periodSummary,
    periodLoading,
  } = useVatMarginScheme(
    periodFrom ? new Date(periodFrom).toISOString() : undefined,
    periodTo ? new Date(`${periodTo}T23:59:59`).toISOString() : undefined
  );
  const { saveHandlers } = useDefaultCRUDHandlers("VAT / Margin Scheme");
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);

  const schemeSetting = generalSettings?.find((s) => s.key === SCHEME_KEY);
  const rateSetting = generalSettings?.find((s) => s.key === RATE_KEY);
  const highValueSetting = generalSettings?.find((s) => s.key === HIGH_VALUE_KEY);
  const highValueAutoSetting = generalSettings?.find((s) => s.key === HIGH_VALUE_AUTO_KEY);
  const openingBalanceSetting = generalSettings?.find((s) => s.key === OPENING_BALANCE_KEY);

  const { register, handleSubmit, reset, watch, formState: { isDirty } } = useForm<FormValues>({
    defaultValues: {
      schemeEnabled: false,
      standardRate: "20",
      highValueAutoStandard: true,
      highValueThreshold: "500",
      openingBalance: "0",
    },
  });

  const watchedEnabled = watch("schemeEnabled");
  const watchedHighValueAuto = watch("highValueAutoStandard");

  useEffect(() => {
    setInfo({
      name: "VAT / Margin Scheme",
      desc: "Configure UK Global Margin Scheme (Global Accounting) and standard VAT rate",
    });
  }, [setInfo]);

  useEffect(() => {
    if (!generalSettings) return;
    if (
      !schemeSetting ||
      !rateSetting ||
      !highValueSetting ||
      !highValueAutoSetting ||
      !openingBalanceSetting
    ) {
      ensureSettings.mutate();
      return;
    }
    reset({
      schemeEnabled: toBool(schemeSetting.value),
      standardRate: rateSetting.value ?? "20",
      highValueAutoStandard: toBool(highValueAutoSetting.value),
      highValueThreshold: highValueSetting.value ?? "500",
      openingBalance: openingBalanceSetting.value ?? "0",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    generalSettings,
    schemeSetting?.value,
    rateSetting?.value,
    highValueSetting?.value,
    highValueAutoSetting?.value,
    openingBalanceSetting?.value,
    reset,
  ]);

  const onSubmit = async (data: FormValues) => {
    const wasEnabled = toBool(schemeSetting?.value);
    try {
      if (!schemeSetting?.id) {
        await create.mutateAsync({
          key: SCHEME_KEY,
          value: data.schemeEnabled ? "true" : "false",
          settingType: SettingType.General,
          inputType: SettingInputType.Toggle,
          required: false,
        } as ISetting);
      } else if (data.schemeEnabled !== wasEnabled) {
        await update.mutateAsync(
          { ...schemeSetting, value: data.schemeEnabled ? "true" : "false" } as ISetting,
          saveHandlers
        );
        if (wasEnabled && !data.schemeEnabled) {
          setCloseConfirmOpen(true);
        }
      }

      if (!rateSetting?.id) {
        await create.mutateAsync({
          key: RATE_KEY,
          value: data.standardRate,
          settingType: SettingType.General,
          inputType: SettingInputType.Number,
          required: false,
        } as ISetting);
      } else if (data.standardRate !== rateSetting.value) {
        await update.mutateAsync(
          { ...rateSetting, value: data.standardRate } as ISetting,
          saveHandlers
        );
      }

      const wasHighValueAuto = toBool(highValueAutoSetting?.value);
      if (!highValueAutoSetting?.id) {
        await create.mutateAsync({
          key: HIGH_VALUE_AUTO_KEY,
          value: data.highValueAutoStandard ? "true" : "false",
          settingType: SettingType.General,
          inputType: SettingInputType.Toggle,
          required: false,
        } as ISetting);
      } else if (data.highValueAutoStandard !== wasHighValueAuto) {
        await update.mutateAsync(
          {
            ...highValueAutoSetting,
            value: data.highValueAutoStandard ? "true" : "false",
          } as ISetting,
          saveHandlers
        );
      }

      if (!highValueSetting?.id) {
        await create.mutateAsync({
          key: HIGH_VALUE_KEY,
          value: data.highValueThreshold,
          settingType: SettingType.General,
          inputType: SettingInputType.Number,
          required: false,
        } as ISetting);
      } else if (data.highValueThreshold !== highValueSetting.value) {
        await update.mutateAsync(
          { ...highValueSetting, value: data.highValueThreshold } as ISetting,
          saveHandlers
        );
      }

      if (!openingBalanceSetting?.id) {
        await create.mutateAsync({
          key: OPENING_BALANCE_KEY,
          value: data.openingBalance,
          settingType: SettingType.General,
          inputType: SettingInputType.Number,
          required: false,
        } as ISetting);
      } else if (data.openingBalance !== openingBalanceSetting.value) {
        await update.mutateAsync(
          { ...openingBalanceSetting, value: data.openingBalance } as ISetting,
          saveHandlers
        );
      }
    } catch {
      // handled by CRUD handlers
    }
  };

  if (loading) {
    return (
      <div className="p-2">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="p-2 space-y-8">
      <ConfirmModal
        open={closeConfirmOpen}
        setOpen={setCloseConfirmOpen}
        title="Close margin scheme pool?"
        confirmButtonText="Close pool"
        message="Scheme has been disabled. Close all open purchase-pool entries, clear the opening balance, and mark margin vehicles as Standard VAT?"
        isLoading={closeScheme.isLoading}
        onConfirm={() => {
          closeScheme.mutate("Scheme disabled via Admin settings", {
            onSettled: () => setCloseConfirmOpen(false),
          });
        }}
      />

      <div>
        <h1 className="text-lg font-medium text-gray-900">VAT / Margin Scheme</h1>
        <p className="text-sm text-gray-600 mt-1">
          When enabled, eligible ELV parts (purchased without VAT) sell under the UK Global Accounting
          Scheme — no separate VAT on sales invoices; VAT due is calculated on the period margin.
          When disabled, parts sales use the standard VAT rate below.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 max-w-lg">
        <div className="rounded-md border border-gray-200 bg-white p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              {...register("schemeEnabled")}
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Global Margin Scheme enabled</span>
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {watchedEnabled
              ? "Eligible stock uses margin scheme; trade (+VAT) purchases stay standard-rated."
              : "All parts sales use the standard VAT rate (channel tax settings override when set)."}
          </p>
        </div>

        <TextInput
          label="Standard VAT rate (%)"
          register={register}
          registerName="standardRate"
          type="number"
          placeholder="20"
        />
        <p className="text-xs text-gray-500 -mt-2">
          Used for purchase VAT previews, self-bills when VAT was charged, and sales when not on margin scheme.
          Current resolved rate: {standardRate}%
        </p>

        <div className="rounded-md border border-gray-200 bg-white p-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              {...register("highValueAutoStandard")}
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Auto-apply standard VAT on high-value parts</span>
          </label>
          <p className="text-xs text-gray-500 mt-1">
            {watchedHighValueAuto
              ? "Margin-scheme parts sold at or above the threshold below get standard VAT on invoices automatically (eBay, PartPro, Shopify, counter)."
              : "High-value sales stay on the vehicle’s VAT treatment unless a part override is set manually."}
          </p>
        </div>

        <TextInput
          label="High-value threshold (£)"
          register={register}
          registerName="highValueThreshold"
          type="number"
          placeholder="500"
          disabled={!watchedHighValueAuto}
        />
        <p className="text-xs text-gray-500 -mt-2">
          Unit sale price at or above this amount triggers the auto-switch. HMRC Global Accounting
          default is £500.
        </p>

        <TextInput
          label="Opening margin pool balance (£)"
          register={register}
          registerName="openingBalance"
          type="number"
          placeholder="0"
        />
        <p className="text-xs text-gray-500 -mt-2">
          Eligible purchase cost already held when you start the scheme (legacy stock). Added to
          the period report opening and closing pool. Cleared to £0 when you close the pool.
        </p>

        <ButtonInput
          label="Save"
          isSubmit={true}
          onClick={undefined}
          classes={undefined}
          disabled={!isDirty}
        />
      </form>

      <div className="border-t border-gray-200 pt-6">
        <h2 className="text-base font-medium text-gray-900 mb-2">Period margin report</h2>
        <p className="text-sm text-gray-600 mb-4">
          Eligible sales − eligible purchases for the date range. VAT due = positive margin × rate/(100+rate).
        </p>
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input
              type="date"
              value={periodFrom}
              onChange={(e) => setPeriodFrom(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input
              type="date"
              value={periodTo}
              onChange={(e) => setPeriodTo(e.target.value)}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
        </div>

        {periodLoading && <LoadingWheel />}
        {periodSummary && (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Opening pool</dt>
              <dd className="font-medium">{PriceFormat(periodSummary.openingPoolBalance)}</dd>
            </div>
            {(periodSummary.configuredOpeningBalance ?? 0) > 0 && (
              <div className="rounded border border-gray-200 bg-white p-3">
                <dt className="text-gray-500">Of which: settings opening balance</dt>
                <dd className="font-medium">{PriceFormat(periodSummary.configuredOpeningBalance)}</dd>
              </div>
            )}
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Purchases entered</dt>
              <dd className="font-medium">{PriceFormat(periodSummary.purchasesEntered)}</dd>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Eligible purchases</dt>
              <dd className="font-medium">{PriceFormat(periodSummary.eligiblePurchases)}</dd>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Eligible sales</dt>
              <dd className="font-medium">{PriceFormat(periodSummary.eligibleSales)}</dd>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Gross margin</dt>
              <dd className="font-medium">{PriceFormat(periodSummary.grossMargin)}</dd>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">VAT due</dt>
              <dd className="font-medium text-primary-700">{PriceFormat(periodSummary.vatDue)}</dd>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Closing pool</dt>
              <dd className="font-medium">{PriceFormat(periodSummary.closingPoolBalance)}</dd>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Purchase entries</dt>
              <dd className="font-medium">{periodSummary.purchaseEntryCount}</dd>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
              <dt className="text-gray-500">Sales lines</dt>
              <dd className="font-medium">{periodSummary.salesLineCount}</dd>
            </div>
          </dl>
        )}
      </div>
    </div>
  );
}
